import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Search, Phone, Mail, Sparkles, MessageSquare, BellOff } from 'lucide-react';
import { GlassCard, CardLabel, Pill, GradientAvatar } from './GlassCard';
import { useLocations } from '../../context/LocationContext';

const statusToTone = {
  new: 'blue',
  contacted: 'amber',
  qualified: 'purple',
  booked: 'hot',
  lost: 'red',
};

export default function LeadsPage() {
  const { api } = useAuth();
  const { activeLocationId, apiQueryParam } = useLocations();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newLead, setNewLead] = useState({ first_name: '', last_name: '', phone: '', email: '', notes: '' });

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, [activeLocationId]);
  const fetchLeads = async () => { try { const { data } = await api.get(`/leads${apiQueryParam()}`); setLeads(data.leads || []); } catch {} };

  const handleAddLead = async () => {
    const payload = { ...newLead };
    if (activeLocationId && activeLocationId !== 'all') payload.location_id = activeLocationId;
    try { await api.post('/leads', payload); setShowModal(false); setNewLead({ first_name: '', last_name: '', phone: '', email: '', notes: '' }); fetchLeads(); } catch {}
  };

  const toggleDnc = async (lead) => {
    try {
      const next = !lead.do_not_contact;
      await api.put(`/leads/${lead.id}`, { do_not_contact: next });
      // Optimistic local update without re-fetching the whole list.
      setLeads((cur) => cur.map((l) => l.id === lead.id ? { ...l, do_not_contact: next } : l));
    } catch {}
  };

  const filtered = leads.filter(l => `${l.first_name} ${l.last_name} ${l.phone}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="leads-page" className="space-y-6 min-w-0">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Leads</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">People who called but haven't committed yet</p>
        </div>
        <button data-testid="add-lead-btn" onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-500 dark:hover:bg-blue-400 text-white text-sm font-medium rounded-full px-4 py-2 flex items-center gap-2 transition-all flex-shrink-0">
          <Plus className="w-4 h-4" /> Add Lead
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
        <input data-testid="leads-search" type="text" placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl pl-10 pr-4 py-3 text-gray-900 dark:text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500" />
      </div>

      <div data-testid="leads-list" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 min-w-0">
        {filtered.length === 0 ? (
          <GlassCard className="col-span-full text-center py-14">
            <p className="text-gray-400 dark:text-slate-500 text-sm">No leads yet. When callers don't commit, they'll appear here.</p>
          </GlassCard>
        ) : filtered.map(lead => {
          const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown caller';
          const tone = statusToTone[lead.status] || 'neutral';
          const timeAgo = lead.created_at ? relTime(lead.created_at) : '';
          return (
            <GlassCard key={lead.id} className="p-4 sm:p-5 min-w-0 overflow-hidden hover:border-blue-300 dark:hover:border-blue-500/30 transition-all">
              {/* Header row: status + time */}
              <div className="flex items-center justify-between mb-4">
                <CardLabel>
                  {lead.status === 'new' ? 'New lead' : lead.status} · {timeAgo || 'recent'}
                </CardLabel>
                <div className="flex items-center gap-2">
                  {lead.do_not_contact && (
                    <span data-testid={`lead-dnc-badge-${lead.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full">
                      <BellOff className="w-3 h-3" /> Opted out
                    </span>
                  )}
                  <Pill tone={tone}>{lead.status === 'booked' ? 'Hot' : lead.status}</Pill>
                </div>
              </div>
              {/* Avatar + identity */}
              <div className="flex items-center gap-3 mb-4">
                <GradientAvatar name={fullName} />
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-gray-900 dark:text-white truncate">{fullName}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                    {lead.phone || ''}{lead.phone && lead.email ? ' · ' : ''}{lead.email || ''}
                  </p>
                </div>
              </div>
              {/* Detail rows */}
              <div className="space-y-2">
                {lead.notes && (
                  <DetailRow icon={MessageSquare} label="Notes" value={lead.notes} accent="blue" />
                )}
                {lead.source && (
                  <DetailRow icon={Sparkles} label="Source" value={lead.source} accent="purple" />
                )}
                {typeof lead.score === 'number' && (
                  <div className="flex items-start gap-3 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-[14px] px-3.5 py-2.5">
                    <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" />
                    <div className="min-w-0 flex-1">
                      <CardLabel>Lead score</CardLabel>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-24 h-1.5 bg-gray-200 dark:bg-white/[0.05] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${lead.score >= 70 ? 'bg-emerald-500' : lead.score >= 40 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.min(100, Math.max(0, lead.score))}%` }} />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">{lead.score}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* DNC toggle — TCPA opt-out */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.06] flex items-center justify-end">
                <button
                  data-testid={`lead-dnc-toggle-${lead.id}`}
                  onClick={() => toggleDnc(lead)}
                  className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${lead.do_not_contact
                    ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/15'
                    : 'bg-gray-50 dark:bg-white/[0.04] text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/[0.08]'}`}
                  title={lead.do_not_contact ? 'Click to allow contact again' : 'Mark as Do Not Contact (skips all SMS)'}
                >
                  <BellOff className="w-3 h-3" />
                  {lead.do_not_contact ? 'Opted out' : 'Mark DNC'}
                </button>
              </div>
            </GlassCard>
          );
        })}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <GlassCard data-testid="add-lead-modal" className="p-6 w-full max-w-md shadow-2xl" glow={false}>
            <h2 className="font-heading text-xl font-medium text-gray-900 dark:text-white mb-4">Add new lead</h2>
            <div className="space-y-3">
              <input data-testid="lead-firstname-input" type="text" placeholder="First name" value={newLead.first_name} onChange={e => setNewLead({...newLead, first_name: e.target.value})}
                className="w-full bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-gray-900 dark:text-white text-sm focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500" />
              <input data-testid="lead-lastname-input" type="text" placeholder="Last name" value={newLead.last_name} onChange={e => setNewLead({...newLead, last_name: e.target.value})}
                className="w-full bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-gray-900 dark:text-white text-sm focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500" />
              <input data-testid="lead-phone-input" type="tel" placeholder="Phone" value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})}
                className="w-full bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-gray-900 dark:text-white text-sm focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500" />
              <input data-testid="lead-email-input" type="email" placeholder="Email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})}
                className="w-full bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-gray-900 dark:text-white text-sm focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500" />
              <textarea data-testid="lead-notes-input" placeholder="Notes" value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})}
                className="w-full bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-gray-900 dark:text-white text-sm focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500 h-20 resize-none" />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 dark:hover:bg-white/[0.08] text-gray-700 dark:text-slate-200 rounded-full py-2.5 text-sm font-medium">Cancel</button>
              <button data-testid="lead-save-btn" onClick={handleAddLead} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-full py-2.5 text-sm font-medium">Save Lead</button>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, accent = 'blue' }) {
  const ring = {
    blue: 'text-blue-500 dark:text-blue-400',
    purple: 'text-purple-500 dark:text-purple-400',
    emerald: 'text-emerald-500 dark:text-emerald-400',
  }[accent] || 'text-blue-400';
  return (
    <div className="flex items-start gap-3 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-[14px] px-3.5 py-2.5 min-w-0 overflow-hidden">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ring}`} />
      <div className="min-w-0 flex-1">
        <CardLabel>{label}</CardLabel>
        <p className="text-sm text-gray-900 dark:text-white truncate mt-0.5 capitalize">{value}</p>
      </div>
    </div>
  );
}

function relTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}
